/**
 * Which part of the US trading day it is, and what a price means in each.
 *
 * The notebook (multi_spread_clean_v17, get_last_price) and the backend
 * (services/priceService.js, services/marketHours.js) price an instrument by
 * the Eastern clock:
 *
 *   before 09:30   the previous session's close; never today's pre-market ticks
 *   09:30 - 16:00  the latest trade
 *   after 16:00    the official close; never an after-hours tick
 *
 * The server already answers that way, and only pushes live ticks during the
 * regular session. This module is the browser's half of the rule: a tick that
 * arrives off-session must not move what is on screen, and every displayed
 * price can say which of those three it is. The names match the backend's
 * sessionPhase(): 'weekend' | 'pre' | 'regular' | 'post'.
 *
 * Everything takes the instant to classify as `now` (ms since the epoch,
 * default the real clock) so a test can stand at any time of any day.
 */

// 09:30 <= t < 16:00, the same half-open window as backend/services/marketHours.js
// (the 16:00 minute is excluded there too).
const RTH_OPEN_S = (9 * 60 + 30) * 60;
const RTH_CLOSE_S = 16 * 60 * 60;

// The closing auction prints a few seconds after 16:00:00 (the backend measures
// its marketHours.CLOSE_PRINT_GRACE_S at 10 s). Anything that re-reads prices
// at the close waits at least this long.
export const CLOSE_PRINT_GRACE_MS = 10 * 1000;

const etFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York',
  weekday: 'short',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  // h23: midnight is "00", not "24" (older ICU renders hour12:false that way).
  hourCycle: 'h23',
});

/** ET wall clock of `now`: { weekday: 'Mon'..'Sun', seconds: since ET midnight }. */
export function etClock(now = Date.now()) {
  const p = {};
  for (const part of etFormatter.formatToParts(new Date(now))) p[part.type] = part.value;
  return {
    weekday: p.weekday,
    seconds: (parseInt(p.hour, 10) % 24) * 3600 + parseInt(p.minute, 10) * 60 + parseInt(p.second, 10),
  };
}

/**
 * 'weekend' (Sat/Sun), 'pre' (before 09:30), 'regular' (09:30 <= t < 16:00) or
 * 'post' (16:00 onwards). There is no exchange calendar: a weekday holiday
 * looks like an ordinary weekday, exactly as it does on the backend.
 */
export function marketSession(now = Date.now()) {
  const { weekday, seconds } = etClock(now);
  if (weekday === 'Sat' || weekday === 'Sun') return 'weekend';
  if (seconds < RTH_OPEN_S) return 'pre';
  if (seconds < RTH_CLOSE_S) return 'regular';
  return 'post';
}

export const isRegularSession = (now = Date.now()) => marketSession(now) === 'regular';

/**
 * How long until the session label can next change, for scheduling a re-check:
 * the next 09:30, 16:00 or ET midnight, by wall-clock arithmetic. A day with a
 * DST shift is an hour out for a target on the far side of 02:00, so callers
 * re-evaluate (and re-schedule) when it fires and the delay is capped.
 */
export function msToNextSessionChange(now = Date.now(), capMs = 60 * 1000) {
  const { seconds } = etClock(now);
  const next = [RTH_OPEN_S, RTH_CLOSE_S, 24 * 3600].find((t) => t > seconds);
  const ms = (next - seconds) * 1000 + 250; // land just past the boundary
  return Math.max(1000, Math.min(ms, capMs));
}

// ---- what the price is -------------------------------------------------------

// The `session` label a server price may carry (services/priceService.js):
//   regular            the latest regular-session trade
//   regular_no_trade   regular hours, no trade yet: the previous close
//   previous_close     the previous session's official close
//   provisional_close  today's close from the last trade, until the daily bar lands
//   official_close     today's close, from the daily bar
//   fallback_daily     the newest daily bar, when there is nothing better
//   bond_last_close    a bond's last stored close
// The last five are reference closes, not live trades: they may be applied at
// any hour because applying one does not move a price with the market.
// Every ticker is treated alike, DATCO ones included.
const REFERENCE_SESSIONS = new Set([
  'previous_close', 'provisional_close', 'official_close', 'fallback_daily', 'bond_last_close',
]);

/**
 * The part of a price-feed frame that may be applied at `now`.
 *
 * Regular hours: all of it. Any other time (before 09:30, from 16:00, any
 * weekend day) the feed is not supposed to be pushing live ticks, and one that
 * arrives anyway (a stale snapshot, a pre-market `last`, a clock that
 * disagrees with the server's by a few seconds) must not re-score the matrix:
 * only entries the server itself labels as a reference close pass.
 *
 * The server's label beats this clock wherever there is one: a reference close
 * passes at any hour, which is how an early close (13:00, no calendar here) and
 * a weekday holiday are handled. The clock only vetoes a live-looking tick, and
 * is the whole rule for an entry that has no label.
 * Returns the same object when nothing was dropped.
 */
export function admitPriceUpdate(prices, now = Date.now()) {
  if (!prices || isRegularSession(now)) return prices;
  let kept = null;
  for (const ticker of Object.keys(prices)) {
    const p = prices[ticker];
    if (p && REFERENCE_SESSIONS.has(p.session)) {
      if (!kept) kept = {};
      kept[ticker] = p;
    }
  }
  return kept || {};
}

/**
 * Short name of what a displayed price is: 'last', 'prev close' or 'close'.
 * The server's own `session` label wins when the entry carries one; otherwise
 * it is the session the value was obtained in (the browser's clock at receipt),
 * which cannot see an early close or a holiday.
 */
export function priceBasis(entrySession, obtainedSession) {
  switch (entrySession) {
    case 'regular':
      return 'last';
    case 'regular_no_trade':
    case 'previous_close':
      return 'prev close';
    case 'provisional_close':
      return 'provisional close';
    case 'official_close':
      return 'close';
    case 'fallback_daily':
    case 'bond_last_close':
      return 'last close';
    default:
      break;
  }
  switch (obtainedSession) {
    case 'regular': return 'last';
    case 'pre': return 'prev close';
    case 'post':
    case 'weekend': return 'close';
    default: return null;
  }
}

/**
 * True when a streamed entry is from a regular session that has since ended and
 * a payload fetched after that end exists to replace it. Until then the entry
 * stays on screen (nothing newer to show), so the swap happens once.
 *
 * @param {{rcv?: number}} streamed   entry from the price feed, stamped with its receipt time
 * @param {{fetchedAt?: number}} payload  the matrix payload's entry for the ticker, stamped likewise
 * @param {number|null} rthEndedAt    when the browser last saw the regular session end; null before it has
 */
export function isSupersededStream(streamed, payload, rthEndedAt) {
  return rthEndedAt != null
    && streamed?.rcv != null && streamed.rcv <= rthEndedAt
    && payload?.fetchedAt != null && payload.fetchedAt > rthEndedAt;
}

/**
 * The accrued dividend a streamed price belongs with.
 *
 * The matrix payload pairs its own price with the accrual of that price's date:
 * the previous session's row for a price that is the previous close (pre-market,
 * before the first trade, weekends). Strip is "live price - accrual", and the
 * client overlays live trades on the payload, so a regular-session trade (today's)
 * must not keep the previous row: on an ex-dividend morning that row still holds
 * the whole dividend. The payload ships the newest row as `accrual_latest` for
 * exactly those entries.
 *
 * @param {object} payloadRow  yields[ticker] from the matrix payload
 * @param {object} streamed    the price-feed entry for the ticker
 */
export function accrualForStream(payloadRow, streamed) {
  if (!payloadRow) return undefined;
  if (streamed?.session === 'regular' && payloadRow.session !== 'regular' && payloadRow.accrual_latest != null) {
    return payloadRow.accrual_latest;
  }
  return payloadRow.accrual;
}

// After the bell the server pushes each ticker's official close as its daily bar lands
// (17:00-18:15 ET). The push carries a price only, so the matrix cells that depend on it
// (Med%, Med Z, Cheap, Rank) stay at what the payload said until the payload is read again.
// A tab asks for that re-read at most this often, and at most this many times between two
// session changes (bars straggle in over more than an hour; each re-read is one request per
// open list, served from the server's 15-minute cache for all but the first tab).
export const OFFICIAL_REREAD_MIN_INTERVAL_MS = 8 * 60 * 1000;
export const OFFICIAL_REREAD_MAX = 5;

/**
 * Tickers whose streamed close is NEWER than the one in the matrix payload this tab holds:
 * the stream says `official_close` for a session whose bar the payload did not have yet (it
 * priced the ticker as a provisional close, or as the close of an earlier session), at a
 * DIFFERENT price (the official bar equals the provisional close to the cent for about 85 %
 * of tickers; a label-only change moves nothing a re-read would fix), and the payload was
 * fetched before that frame arrived. Re-reading the payload fixes all of them, and every
 * cell of a row depends on the other tickers' prices too; once it has been re-read after the
 * frame, a ticker no longer qualifies.
 *
 * @param {object} wsPrices  state.tickerTape.prices (entries stamped with `rcv`)
 * @param {object} yields    state.spreads.yields (rows stamped with `fetchedAt`)
 * @param {string[]} tickers the tickers of the open matrices
 * @param {number} [sinceMs] only frames received after this instant count: a re-read requested at
 *   `sinceMs` is already on its way for everything received before it, and the payload it is about
 *   to replace still looks stale until it lands
 */
export function officialAheadOfPayload(wsPrices, yields, tickers, sinceMs = 0) {
  const out = [];
  for (const t of tickers) {
    const w = wsPrices?.[t];
    const y = yields?.[t];
    if (!w || !y || w.session !== 'official_close' || !w.sessionDate) continue;
    if (y.session == null || typeof y.fetchedAt !== 'number' || typeof w.rcv !== 'number') continue;
    if (w.rcv <= sinceMs) continue;                           // covered by a re-read already requested
    if (y.fetchedAt >= w.rcv) continue;                       // the payload was read after this frame
    if (typeof w.price !== 'number' || typeof y.price !== 'number' || w.price === y.price) continue;   // same price: label only
    if (y.session !== 'official_close' || (y.sessionDate || '') < w.sessionDate) out.push(t);
  }
  return out;
}

/**
 * How long to wait before the re-read: a minute and a bit (the server's matrix cache key moves
 * to the official-close basis within 30 s of the first bar), jittered so open tabs do not land
 * together, and not sooner than the minimum interval after the previous one. null = do not.
 */
export function officialRereadDelayMs({ now, lastAt, count, jitter = Math.random() }) {
  if (count >= OFFICIAL_REREAD_MAX) return null;
  const base = 60 * 1000 + CLOSE_PRINT_GRACE_MS + jitter * 25 * 1000;
  const wait = lastAt != null ? Math.max(0, lastAt + OFFICIAL_REREAD_MIN_INTERVAL_MS - now) : 0;
  return Math.max(base, wait);
}

/**
 * The clean price (price - accrued dividend) of a streamed price, or null to leave the
 * payload's value alone. Only for a ticker that HAS a clean price (clean_price > 0 in the
 * payload: bonds and preferreds; price-only tickers such as the DATCO ETFs have 0 and render
 * "-"). A zero accrual is a real value there: the cumulative accrual resets to 0 on an
 * ex-dividend date, and a live trade that day is its own clean price.
 */
export function cleanPriceFromStream(payloadRow, accrual, price) {
  if (!(price > 0) || accrual == null) return null;
  if (accrual > 0) return price - accrual;
  return accrual === 0 && payloadRow?.clean_price > 0 ? price : null;
}
